import React, { useRef } from 'react';
import PreviewLoading from './PreviewLoading';
import type { PreviewHandlers } from './contentPreviewHandlers';
import { useInvestorPreviewBridge } from './useInvestorPreviewBridge';

/** Served same-origin by the admin app (see scripts/create-vite-dev-config.ts and nginx). */
export const INVESTOR_PREVIEW_URL = '/investor-preview/';

interface InvestorPreviewFrameProps {
  /** Which investor view the iframe renders, e.g. 'fund-updates-content'. */
  view: string;
  /** Props for that view; re-sent to the iframe whenever they change. */
  viewProps: Record<string, unknown>;
  /** The only calls the iframe may make; anything else is answered with an error. */
  handlers: PreviewHandlers;
  title: string;
  src?: string;
}

/**
 * Renders an investor page inside an isolated, investor-built iframe, so it looks exactly as
 * investors see it. Borderless and sized to its content, so it reads as part of the page. The
 * iframe holds no credentials: its data calls arrive here and are answered by `handlers`.
 */
export default function InvestorPreviewFrame({
  view,
  viewProps,
  handlers,
  title,
  src = INVESTOR_PREVIEW_URL,
}: InvestorPreviewFrameProps) {
  const frameRef = useRef<HTMLIFrameElement>(null);
  const { height, rendered } = useInvestorPreviewBridge(frameRef, view, viewProps, handlers);
  // The iframe stays mounted (it has to load) but hidden behind the spinner until its view is up.
  return (
    <>
      {!rendered && <PreviewLoading />}
      <iframe
        ref={frameRef}
        src={src}
        title={title}
        data-testid="investor-preview-frame"
        aria-busy={!rendered}
        style={{
          display: 'block',
          width: '100%',
          height: rendered ? height : 0,
          border: 0,
          visibility: rendered ? 'visible' : 'hidden',
        }}
      />
    </>
  );
}
